import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Row,
  Col,
  Statistic,
  Empty,
  Spin,
  Button,
  Space,
  Alert,
  Typography,
  Popconfirm,
  message,
  Divider,
  Select,
} from "antd";
import { ExportOutlined, LinkOutlined, ReloadOutlined, UserAddOutlined } from "@ant-design/icons";
import { Company } from "../types/company";
import { Invoice } from "../types/invoice";
import { supabase } from "../lib/supabase";
import { fetchAdminMoneybirdContacts, adminMoneybirdUpsertContactFromCompany } from "../services/moneybirdService";
import type { AdminMoneybirdContactsResponse } from "../services/moneybirdService";
import {
  moneybirdContactDisplayName,
  scoreMoneybirdNameMatch,
  type MoneybirdContactPreview,
} from "../utils/moneybirdContactNameMatch";

/** Fallback if administration id was not returned yet (matches historical hardcoded links). */
const DEFAULT_MONEYBIRD_ADMIN_ID = "443982818600552286";

interface AdminMoneybirdIntegrationSectionProps {
  company: Company;
  companyInvoices: Invoice[];
  invoicesLoading: boolean;
  invoiceStats: {
    totalRevenue: number;
    outstandingBalance: number;
    invoiceCount: number;
  };
  /** Called after linking, updating, or removing the Moneybird contact id so the modal stays in sync. */
  onContactSaved?: () => void | Promise<void>;
}

const AdminMoneybirdIntegrationSection: React.FC<
  AdminMoneybirdIntegrationSectionProps
> = ({
  company,
  companyInvoices,
  invoicesLoading,
  invoiceStats,
  onContactSaved,
}) => {
  const companyId = company.id;
  const [savingContact, setSavingContact] = useState(false);
  const [upsertingFromCompany, setUpsertingFromCompany] = useState(false);
  const [removingContact, setRemovingContact] = useState(false);
  const [mbContacts, setMbContacts] = useState<MoneybirdContactPreview[]>([]);
  const [mbAdminId, setMbAdminId] = useState("");
  const [mbTruncated, setMbTruncated] = useState(false);
  const [loadingMbContacts, setLoadingMbContacts] = useState(false);
  const [selectedMbId, setSelectedMbId] = useState<string | undefined>(
    undefined
  );

  const companyRef = useRef(company);
  companyRef.current = company;

  const applyFetchedContacts = useCallback(
    (data: AdminMoneybirdContactsResponse, options?: { showToast?: boolean }) => {
      setMbContacts(data.contacts);
      setMbAdminId(data.administration_id);
      setMbTruncated(data.truncated);
      if (options?.showToast) {
        message.success(`Loaded ${data.contacts.length} Moneybird contact(s)`);
      }
      const c = companyRef.current;
      const sorted = [...data.contacts]
        .map((x) => ({ contact: x, score: scoreMoneybirdNameMatch(c.name ?? "", x) }))
        .sort((a, b) => b.score - a.score);
      if (c.moneybird_contact_id) {
        setSelectedMbId(c.moneybird_contact_id);
      } else if (sorted.length > 0 && sorted[0].score >= 55) {
        setSelectedMbId(sorted[0].contact.id);
      } else if (sorted.length > 0) {
        setSelectedMbId(undefined);
      }
    },
    []
  );

  const reloadMoneybirdContacts = useCallback(async () => {
    if (companyId == null) return;
    setLoadingMbContacts(true);
    try {
      const { data, error } = await fetchAdminMoneybirdContacts();
      if (error || !data) {
        message.error(error ?? "Failed to load Moneybird contacts");
        return;
      }
      applyFetchedContacts(data, { showToast: true });
    } catch (e) {
      console.error(e);
      message.error("Failed to load Moneybird contacts");
    } finally {
      setLoadingMbContacts(false);
    }
  }, [companyId, applyFetchedContacts]);

  const upsertFromCompanyRecord = useCallback(async () => {
    if (companyId == null) return;
    setUpsertingFromCompany(true);
    try {
      const { data, error } = await adminMoneybirdUpsertContactFromCompany(
        companyId
      );
      if (error || !data) {
        message.error(error ?? "Could not create or link Moneybird contact");
        return;
      }
      message.success(data.message);
      await onContactSaved?.();
      setLoadingMbContacts(true);
      try {
        const { data: list, error: listErr } =
          await fetchAdminMoneybirdContacts();
        if (!listErr && list) {
          applyFetchedContacts(list, { showToast: false });
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingMbContacts(false);
      }
    } catch (e) {
      console.error(e);
      message.error("Could not create or link Moneybird contact");
    } finally {
      setUpsertingFromCompany(false);
    }
  }, [companyId, onContactSaved, applyFetchedContacts]);

  useEffect(() => {
    const linked = company.moneybird_contact_id ?? "";
    setSelectedMbId(linked ? linked : undefined);
  }, [company.id, company.moneybird_contact_id]);

  useEffect(() => {
    if (companyId == null) return;
    setMbContacts([]);
    setMbAdminId("");
    setMbTruncated(false);
    let cancelled = false;
    setLoadingMbContacts(true);
    (async () => {
      try {
        const { data, error } = await fetchAdminMoneybirdContacts();
        if (cancelled) return;
        if (error || !data) {
          message.error(error ?? "Failed to load Moneybird contacts");
          return;
        }
        applyFetchedContacts(data, { showToast: false });
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          message.error("Failed to load Moneybird contacts");
        }
      } finally {
        if (!cancelled) {
          setLoadingMbContacts(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, applyFetchedContacts]);

  const moneybirdAdminHrefId = mbAdminId || DEFAULT_MONEYBIRD_ADMIN_ID;

  const scoredContacts = useMemo(() => {
    const name = company.name ?? "";
    return [...mbContacts]
      .map((c) => ({ c, score: scoreMoneybirdNameMatch(name, c) }))
      .sort((a, b) => b.score - a.score);
  }, [mbContacts, company.name]);

  const saveContactId = async () => {
    if (companyId == null) {
      message.error("Missing company id");
      return;
    }
    const trimmed = selectedMbId?.trim();
    if (!trimmed) {
      message.warning("Select a Moneybird contact from the list");
      return;
    }
    setSavingContact(true);
    try {
      const { error } = await supabase
        .from("company")
        .update({ moneybird_contact_id: trimmed })
        .eq("id", companyId);
      if (error) throw error;
      message.success("Moneybird contact saved");
      await onContactSaved?.();
    } catch (e) {
      console.error(e);
      message.error("Failed to save Moneybird contact");
    } finally {
      setSavingContact(false);
    }
  };

  const removeContactId = async () => {
    if (companyId == null) {
      message.error("Missing company id");
      return;
    }
    setRemovingContact(true);
    try {
      const { error } = await supabase
        .from("company")
        .update({ moneybird_contact_id: null })
        .eq("id", companyId);
      if (error) throw error;
      message.success("Moneybird contact removed");
      setSelectedMbId(undefined);
      await onContactSaved?.();
    } catch (e) {
      console.error(e);
      message.error("Failed to remove Moneybird contact");
    } finally {
      setRemovingContact(false);
    }
  };

  return (
    <Card
      title="Moneybird Integration"
      style={{ marginBottom: 16 }}
      extra={
        company.moneybird_contact_id ? (
          <Button
            type="primary"
            style={{
              backgroundColor: "#21b36a",
              borderColor: "#21b36a",
            }}
            icon={<ExportOutlined />}
            href={`https://moneybird.com/${moneybirdAdminHrefId}/contacts/${company.moneybird_contact_id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in Moneybird
          </Button>
        ) : null
      }
    >
      {companyId == null ? (
        <Alert type="error" message="Cannot link contact: company id is missing." />
      ) : (
        <>
          {!company.moneybird_contact_id ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="No Moneybird contact linked"
              description={
                <>
                  Contacts load automatically from Moneybird when you open this
                  tab; they are sorted with the closest name matches first.
                  Choose a contact below, then link. Or use{" "}
                  <strong>Find or create from company</strong> to match by EFF
                  id / exact name in Moneybird, or create a new contact from
                  this company&apos;s details. Use reload if you created a new
                  contact in Moneybird.
                </>
              }
            />
          ) : (
            <Space
              direction="vertical"
              size="small"
              style={{ width: "100%", marginBottom: 16 }}
            >
              <Typography.Text type="secondary">
                Linked contact id
              </Typography.Text>
              <Typography.Paragraph copyable style={{ marginBottom: 0 }}>
                {company.moneybird_contact_id}
              </Typography.Paragraph>
            </Space>
          )}

          <Space direction="vertical" style={{ width: "100%", marginBottom: 12 }}>
            <Button
              icon={<ReloadOutlined />}
              loading={loadingMbContacts}
              onClick={reloadMoneybirdContacts}
            >
              Reload contacts from Moneybird
            </Button>

            {!company.moneybird_contact_id ? (
              <>
                <Button
                  type="default"
                  icon={<UserAddOutlined />}
                  loading={upsertingFromCompany}
                  disabled={
                    !company.name?.trim() ||
                    upsertingFromCompany ||
                    savingContact
                  }
                  onClick={upsertFromCompanyRecord}
                >
                  Find or create from company
                </Button>
                <Typography.Paragraph
                  type="secondary"
                  style={{ marginBottom: 0, maxWidth: 560, fontSize: 13 }}
                >
                  Looks up Moneybird by customer id{" "}
                  <Typography.Text code>
                    EFF-{company.id ?? "?"}
                  </Typography.Text>
                  , then by exact company name. If nothing matches, creates a
                  new contact (address, VAT, KvK, invoice email when set, EORI
                  and Art. 23 in the note) and links it.
                </Typography.Paragraph>
              </>
            ) : null}

            {loadingMbContacts && mbContacts.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <Spin tip="Loading Moneybird contacts…" />
              </div>
            ) : mbContacts.length > 0 ? (
              <>
                {mbTruncated ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="Contact list was truncated"
                    description="Only the first 4,000 contacts were loaded. Use the search box to narrow down if the contact is missing."
                    style={{ marginBottom: 8 }}
                  />
                ) : null}
                <Typography.Text type="secondary" style={{ display: "block" }}>
                  {company.name
                    ? `Sorted by similarity to “${company.name}”.`
                    : "Add a company name on the General tab for better auto-ranking."}
                </Typography.Text>
                <Select
                  showSearch
                  allowClear
                  placeholder="Search and select a Moneybird contact"
                  style={{ width: "100%", maxWidth: 560 }}
                  value={selectedMbId}
                  options={scoredContacts.map(({ c, score }) => {
                    const display = moneybirdContactDisplayName(c);
                    const email = c.email ? ` — ${c.email}` : "";
                    const star = score >= 65 ? "★ " : "";
                    return {
                      value: c.id,
                      label: `${star}${display} — ${c.id}${email}`,
                    };
                  })}
                  onChange={(value) => {
                    setSelectedMbId(value);
                  }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  ★ = likely match to this company name (heuristic, not exact).
                </Typography.Text>
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  loading={savingContact}
                  disabled={!selectedMbId}
                  onClick={saveContactId}
                  style={{ marginTop: 4 }}
                >
                  {company.moneybird_contact_id ? "Update link" : "Link contact"}
                </Button>
              </>
            ) : null}
          </Space>

          {company.moneybird_contact_id ? (
            <Popconfirm
              title="Remove Moneybird contact from this company?"
              okText="Remove"
              okButtonProps={{ danger: true }}
              onConfirm={removeContactId}
            >
              <Button danger loading={removingContact} style={{ marginBottom: 16 }}>
                Remove link
              </Button>
            </Popconfirm>
          ) : null}
        </>
      )}

      <Divider style={{ margin: "8px 0 16px" }} />

      {invoicesLoading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin />
        </div>
      ) : (
        <>
          {/* Invoice Statistics */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic
                title="Total Revenue"
                value={invoiceStats.totalRevenue}
                precision={2}
                prefix="€"
                valueStyle={{ color: "#3f8600" }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Outstanding Balance"
                value={invoiceStats.outstandingBalance}
                precision={2}
                prefix="€"
                valueStyle={{
                  color:
                    invoiceStats.outstandingBalance > 0 ? "#cf1322" : "#3f8600",
                }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Total Invoices"
                value={invoiceStats.invoiceCount}
                valueStyle={{ color: "#1890ff" }}
              />
            </Col>
          </Row>

          {/* Invoice Table */}
          <Table
            dataSource={companyInvoices}
            rowKey="id"
            pagination={{ pageSize: 5, showSizeChanger: false }}
            size="small"
            columns={[
              {
                title: "Invoice Date",
                dataIndex: "created_at",
                key: "created_at",
                render: (date) => new Date(date).toLocaleDateString(),
                width: 120,
              },
              {
                title: "Booking",
                key: "booking",
                render: (_, record) => {
                  if (!(record as any).booking_id) return "-";

                  const transportType = (record as any).booking
                    ?.transport_type_info
                    ? Array.isArray((record as any).booking.transport_type_info)
                      ? (record as any).booking.transport_type_info[0]
                          ?.abbreviation || ""
                      : (record as any).booking.transport_type_info
                          .abbreviation || ""
                    : "";

                  return `EFF${(record as any).booking_id}${transportType}`;
                },
                width: 120,
              },
              {
                title: "Status",
                dataIndex: "moneybird_payment_status",
                key: "status",
                render: (status) => {
                  const statusColors = {
                    paid: "success",
                    open: "processing",
                    overdue: "error",
                    partial: "warning",
                    cancelled: "default",
                  };
                  return (
                    <Tag color={(statusColors as any)[status] || "default"}>
                      {status || "pending"}
                    </Tag>
                  );
                },
                width: 100,
              },
              {
                title: "Amount",
                key: "amount",
                render: (_, record) => {
                  const total =
                    (record as any).lines?.reduce((sum: number, line: any) => {
                      return (
                        sum +
                        line.quantity *
                          line.unit_price *
                          (1 + line.vat_percentage / 100)
                      );
                    }, 0) || 0;
                  return `€${total.toFixed(2)}`;
                },
                width: 100,
                align: "right",
              },
              {
                title: "Due Date",
                dataIndex: "moneybird_due_date",
                key: "due_date",
                render: (date) =>
                  date ? new Date(date).toLocaleDateString() : "-",
                width: 120,
              },
            ]}
            locale={{
              emptyText: (
                <Empty
                  description="No invoices found for this company"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        </>
      )}
    </Card>
  );
};

export default AdminMoneybirdIntegrationSection;







